import { Plus, Trash2 } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import Modal from "../../../components/modal/Modal";
import { themeQuartz } from "ag-grid-community";
import { useEquipmentStore } from "../../../store/store";
import { AgGridReact } from "ag-grid-react";
import images from "../../../assets/images";
import { useAuth } from "../../../auth/AuthProvider";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

type AssetEquipmentSubcategoryFormData = {
  subcategoryName: string;
  categoryId: string;
  description?: string;
};

const emptyForm: AssetEquipmentSubcategoryFormData = {
  subcategoryName: "",
  categoryId: "",
  description: "",
};

const AssetEquipmentSubcategory: React.FC = () => {
  const [rowData, setRowData] = useState<(AssetEquipmentSubcategoryFormData & { categoryName?: string, _id?: string })[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const { roleData } = useAuth();

  const {
    equipmentData,
    assetSubcategoryData,
    allAssetsEquipment,
    allAssetSubcategories,
    createAssetSubcategory,
    deleteAssetSubcategory,
    updateAssetSubcategory,
  } = useEquipmentStore();

  // Use equipment data as asset categories (they represent the asset equipment types)
  const assetCategories = equipmentData;

  const [formData, setFormData] = useState<AssetEquipmentSubcategoryFormData>(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);
  const [subcategoryNameExists, setSubcategoryNameExists] = useState(false);

  useEffect(() => {
    const resData = assetSubcategoryData.map((subcategory) => {
      const category = assetCategories.find(cat => cat._id === subcategory.categoryId);
      return {
        ...subcategory,
        categoryName: category?.equipmentName || "Unknown Category",
      };
    });
    setRowData(resData);
  }, [assetSubcategoryData, assetCategories]);

  useEffect(() => {
    allAssetsEquipment();
    allAssetSubcategories();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "subcategoryName") {
      const exists = assetSubcategoryData.some(
        (subcat) =>
          subcat.subcategoryName?.trim().toLowerCase() === value.trim().toLowerCase() &&
          subcat.categoryId === formData.categoryId &&
          (!editId || subcat._id !== editId)
      );
      setSubcategoryNameExists(exists);
    }
  };

  const handleEdit = (data: any) => {
    setFormData({
      subcategoryName: data.subcategoryName,
      categoryId: data.categoryId,
      description: data.description || "",
    });
    setIsOpen(true);
    setEditId(data._id);
  };

  const handleDelete = async (id: string) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this asset equipment subcategory? This action cannot be undone."
    );

    if (confirmed) {
      await deleteAssetSubcategory(id);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.subcategoryName.trim() || !formData.categoryId) {
      alert("Please fill in all required fields.");
      return;
    }


    if (subcategoryNameExists) {
      alert("Subcategory name already exists in this category.");
      return;
    }

    try {
      if (editId) {
        await updateAssetSubcategory(editId, formData);
      } else {
        await createAssetSubcategory(formData);
      }

      setFormData(emptyForm);
      setEditId(null);
      setIsOpen(false);
      setSubcategoryNameExists(false);
    } catch (error) {
      console.error("Failed to save asset equipment subcategory:", error);
    }
  };

  const columnDefs = useMemo(() => ([
    {
      field: "categoryName",
      headerName: "Asset Category",
      sortable: true,
      minWidth: 150,
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      filter: true,
      cellStyle: {
        padding: "10px 5px 0 10px",
        textAlign: "left",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },
    {
      field: "subcategoryName",
      headerName: "Subcategory Name",
      sortable: true,
      minWidth: 150,
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      filter: true,
      cellStyle: {
        padding: "10px 5px 0 10px",
        textAlign: "left",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },
    {
      field: "description",
      headerName: "Description",
      sortable: false,
      minWidth: 200,
      flex: 2,
      headerStyle: { backgroundColor: "#e9f5ff" },
      filter: false,
      cellStyle: {
        padding: "10px 5px 0 10px",
        textAlign: "left",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      tooltipField: "description",
    },
    {
      field: "action",
      headerName: "Action",
      cellStyle: { padding: "10px 15px 0 0", textAlign: "left" },
      headerStyle: { backgroundColor: "#e9f5ff" },
      flex: 0,
      minWidth: 120,
      maxWidth: 120,
      cellRenderer: (params: any) => (
        <div className="flex gap-x-3 pt-2">
          <button
            onClick={() => handleEdit(params.data)}
            className="p-1 hover:bg-gray-100 rounded"
            title="Edit Asset Equipment Subcategory"
          >
            <img className="iconTd w-5 h-5" src={images.editIcon} alt="edit" />
          </button>
          <button
            onClick={() => handleDelete(params.data._id)}
            className="p-1 hover:bg-red-100 rounded"
            title="Delete Asset Equipment Subcategory"
          >
            <Trash2 className="text-red-500" size={20} />
          </button>
        </div>
      ),
      filter: false,
      sortable: false,
    },
  ] as any), [handleEdit, handleDelete]);

  const defaultColDef = useMemo(() => {
    return {
      editable: false,
      tooltipComponentParams: {
        color: "#ececec",
      },
    };
  }, []);

  const theme = useMemo(() => {
    return gridTheme;
  }, []);

  return (
    <>
      <div className="px-2 py-4 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h2 className="ml-2 text-2xl font-semibold text-gray-800">Asset Equipment Subcategories</h2>
          <button
            onClick={() => {
              setIsOpen(true);
              setFormData(emptyForm);
              setEditId(null);
              setSubcategoryNameExists(false);
            }}
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white px-4 py-2 rounded-full hover:bg-[#0e9895] transition-colors"
          >
            <Plus size={20} /> Add Subcategory
          </button>
        </div>

        <div className="tableBlock w-full">
          <div className="card">
            {rowData?.length > 0 ? (
              <div className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-lg">
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={65}
                  rowHeight={60}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                  pagination={true}
                  paginationPageSize={20}
                />
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-64">
                <p className="text-gray-500 text-lg mb-4">No asset equipment subcategories added yet.</p>
                <button
                  onClick={() => {
                    setIsOpen(true);
                    setFormData(emptyForm);
                    setEditId(null);
                    setSubcategoryNameExists(false);
                  }}
                  className="bg-[#0fa9a6] text-white px-4 py-2 rounded-full hover:bg-[#0e9895] transition-colors"
                >
                  Add First Subcategory
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setFormData(emptyForm);
          setEditId(null);
          setSubcategoryNameExists(false);
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-2xl justify-center items-center rounded-xl relative"
      >
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-4">
            {editId ? "Edit Asset Equipment Subcategory" : "Add New Asset Equipment Subcategory"}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Asset Equipment Category <span className="text-red-500">*</span>
              </label>
              <select
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent"
              >
                <option value="">Select a category</option>
                {assetCategories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.equipmentName}
                  </option>
                ))}
              </select>
            </div>

            {/* Subcategory Name */}
            <div>
              {subcategoryNameExists && (
                <div className="text-red-600 text-sm mb-1 font-semibold">
                  Subcategory name already exists in this category
                </div>
              )}
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subcategory Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="subcategoryName"
                value={formData.subcategoryName}
                onChange={handleChange}
                placeholder="Enter subcategory name"
                required
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent ${subcategoryNameExists ? "border-red-500" : "border-gray-300"
                  }`}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter subcategory description (optional)"
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent resize-vertical"
              />
            </div>
          </form>
        </div>

        <div className="flex justify-end gap-4 mt-3 p-4 bg-[#5A7192] w-full rounded-b-lg">
          <button
            onClick={() => {
              setIsOpen(false);
              setFormData(emptyForm);
              setEditId(null);
              setSubcategoryNameExists(false);
            }}
            className="bg-[#8497B0] font-semibold text-white px-6 py-2 rounded-full hover:opacity-90 transition-opacity"
          >
            CANCEL
          </button>
          <button
            onClick={handleSubmit}
            disabled={subcategoryNameExists || !formData.subcategoryName.trim() || !formData.categoryId}
            className="bg-[#00A8A8] font-semibold text-white px-6 py-2 rounded-full hover:opacity-90 transition-opacity disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {editId ? "UPDATE" : "CREATE"}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default AssetEquipmentSubcategory;
